import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { PathGraphContext } from '../../common/widgets/common/PathGraph/PathGraph';
import { usePrevious } from '../../common/hooks/use-previous';
import { useSelector } from 'react-redux';
import { groupBy } from 'lodash';
import { StoreState } from '@mm/state/store-interfaces';
import { PlatformType } from '@mm/state/model/CampaignsModel';

export const availableWidgets = [
  WidgetTypes.QUICK_QUESTION,
  WidgetTypes.TEXT,
  WidgetTypes.IMAGE,
  WidgetTypes.ATTACHMENT,
  WidgetTypes.AUDIO,
  WidgetTypes.DELAY,
  WidgetTypes.GALLERY,
  WidgetTypes.GIF,
  WidgetTypes.VIDEO,
  WidgetTypes.OTN,
  WidgetTypes.OPT_IN,
  WidgetTypes.FORM,
  WidgetTypes.SMS_OPT_IN,
  WidgetTypes.SMS_OPT_IN_V2,
  WidgetTypes.SMS_OPT_IN_REPLY,
  WidgetTypes.NAVIGATE,
];

export const useDialoguePreview = ({
  dialogueId,
  platforms,
  customAvailableWidgets,
  setWidgetCount,
}: {
  dialogueId: number;
  platforms?: PlatformType[];
  customAvailableWidgets?: WidgetTypes[];
  setWidgetCount?: (v: number) => void;
}) => {
  const { isWidgetActive, getWidgetActiveDialogueInfo } =
    useContext(PathGraphContext);
  const localAvailableWidgets = customAvailableWidgets ?? availableWidgets;

  const widgets = useSelector<StoreState, WidgetModel[]>(
    store => store.widget.entities,
  );

  const getListOfWidgetsTillActive = useCallback(
    (arr: WidgetModel[]) => {
      const maybeShortenedList: WidgetModel[] = [];
      let index = 0;
      while (index < arr.length) {
        const currentWidget = arr[index];
        maybeShortenedList.push(currentWidget);
        if (isWidgetActive?.(currentWidget)) {
          break;
        }
        index += 1;
      }
      return maybeShortenedList;
    },
    [isWidgetActive],
  );

  const filteredWidgetsByPageId = useMemo(() => {
    return groupBy(
      widgets
        ?.filter(w => availableWidgets.includes(w.type))
        .filter(
          widget =>
            widget.validated.facebook &&
            !widget.validated.buttonDialogueInValid,
        )
        .filter(
          widget =>
            !platforms?.length ||
            platforms.every(p => widget.supportedByPlatforms[p]),
        )
        .sort((f, s) => f.sortOrder - s.sortOrder),
      'pageId',
    );
  }, [widgets, platforms, availableWidgets]);

  const prevFilteredWidgetsByPageId = usePrevious<{
    [index: string]: WidgetModel[];
  }>(filteredWidgetsByPageId);

  const fullFilteredWidgetsByPageId = useMemo(() => {
    const removePageId = Object.keys(prevFilteredWidgetsByPageId).filter(x => {
      return !Object.keys(filteredWidgetsByPageId).includes(x);
    });
    let widgetsByPageId = Object.keys(filteredWidgetsByPageId).reduce(
      (acc: Map<number, WidgetModel[]>, key) => {
        return new Map(acc).set(
          Number(key),
          filteredWidgetsByPageId[key] as WidgetModel[],
        );
      },
      new Map<number, WidgetModel[]>(),
    );
    removePageId.forEach(key => {
      widgetsByPageId = new Map(widgetsByPageId).set(Number(key), []);
    });

    return widgetsByPageId;
  }, [filteredWidgetsByPageId]);

  const getSortedWidgets = useCallback(
    (pageId: number) => {
      return widgets
        ?.filter(w => localAvailableWidgets.includes(w.type))
        .filter(widget => widget.pageId === pageId)
        .filter(
          widget =>
            widget.validated.facebook &&
            !widget.validated.buttonDialogueInValid,
        )
        .filter(
          widget =>
            !platforms?.length ||
            platforms.every(p => widget.supportedByPlatforms[p]),
        )
        .sort((f, s) => f.sortOrder - s.sortOrder);
    },
    [localAvailableWidgets, widgets, platforms],
  );

  const getFlattenedWidgetsList = useCallback<(id: number) => WidgetModel[]>(
    levelDialogueId => {
      const dialogueWidgets = getSortedWidgets(levelDialogueId);
      const dialogueWidgetsToShow = getListOfWidgetsTillActive(dialogueWidgets);

      if (!dialogueWidgetsToShow.length) {
        return dialogueWidgetsToShow;
      }

      const lastWidgetToShow =
        dialogueWidgetsToShow[dialogueWidgetsToShow.length - 1];

      const widgetWithOpenedDialogue =
        dialogueWidgetsToShow.length !== dialogueWidgets.length
          ? lastWidgetToShow
          : isWidgetActive?.(lastWidgetToShow)
          ? lastWidgetToShow
          : undefined;

      if (!widgetWithOpenedDialogue || !getWidgetActiveDialogueInfo) {
        return dialogueWidgetsToShow;
      }

      const { nextLevelDialogueId, isCircular } = getWidgetActiveDialogueInfo(
        widgetWithOpenedDialogue,
      );

      return nextLevelDialogueId && !isCircular
        ? [
            ...dialogueWidgetsToShow,
            ...getFlattenedWidgetsList(nextLevelDialogueId),
          ]
        : dialogueWidgetsToShow;
    },
    [
      getSortedWidgets,
      getListOfWidgetsTillActive,
      isWidgetActive,
      getWidgetActiveDialogueInfo,
    ],
  );

  const preview: WidgetModel[] = useMemo(
    () => (dialogueId ? getFlattenedWidgetsList(dialogueId) : []),
    [dialogueId, getFlattenedWidgetsList],
  );

  useEffect(() => {
    setWidgetCount?.(preview.length);
  }, [preview.length]);

  return {
    preview,
    widgetsByPageId: fullFilteredWidgetsByPageId,
  };
};
